import { useMemo, useState } from "react";
import { chapters } from "@/mocks/home";

const volumes = [
  { key: "全部", label: "全部章节" },
  { key: "卷一", label: "卷一 · 初入职场" },
  { key: "卷二", label: "卷二 · 并肩同行" },
  { key: "卷三", label: "卷三 · 新的开始" },
];

const volOf = (no: number) => (no <= 6 ? "卷一" : no <= 12 ? "卷二" : "卷三");

const tagStyle = (tag: string) => {
  if (tag === "免费") return "bg-secondary-100 text-secondary-800 border-secondary-200";
  if (tag === "热门") return "bg-primary-100 text-primary-800 border-primary-200";
  if (tag === "最新") return "bg-accent-100 text-accent-900 border-accent-300";
  return "";
};

export default function ChapterSection() {
  const [active, setActive] = useState("全部");
  const [keyword, setKeyword] = useState("");

  const list = useMemo(() => {
    const kw = keyword.trim();
    return chapters.filter((c) => {
      const okVol = active === "全部" || volOf(c.no) === active;
      const okKw = !kw || c.title.includes(kw) || String(c.no).includes(kw);
      return okVol && okKw;
    });
  }, [active, keyword]);

  return (
    <section id="chapters" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-14 md:px-6 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="flex items-center gap-2 text-xs font-label tracking-wide text-secondary-600">
              <i className="ri-list-check-2" />
              章节列表
            </span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
              按卷查找，按序阅读
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              全书共 326 章，分为五卷。以下为最新连载段落，点击任意章节即可进入阅读。
            </p>
          </div>

          <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 py-1 pl-3 pr-1">
            <i className="ri-search-line text-sm text-foreground-500" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="输入章节号或篇名"
              className="w-40 bg-transparent text-sm font-label text-foreground-800 outline-none placeholder:text-foreground-400 md:w-52"
            />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {volumes.map((v) => (
            <button
              key={v.key}
              type="button"
              onClick={() => setActive(v.key)}
              className={`whitespace-nowrap rounded-md border px-3.5 py-2 text-sm font-label transition-colors ${
                active === v.key
                  ? "border-primary-500 bg-primary-500 text-background-50"
                  : "border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        {list.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((c) => (
              <button
                key={c.no}
                type="button"
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-4 py-3.5 text-left transition-colors hover:border-primary-300 hover:bg-background-50"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 font-heading text-sm font-bold text-primary-600 ring-1 ring-background-300">
                  {String(c.no).padStart(3, "0")}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate font-heading text-sm font-bold text-foreground-950">
                      {c.title}
                    </span>
                    {c.tag && (
                      <span
                        className={`shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-label ${tagStyle(
                          c.tag,
                        )}`}
                      >
                        {c.tag}
                      </span>
                    )}
                  </span>
                  <span className="mt-1 flex items-center gap-2 text-xs font-label text-foreground-500">
                    <span>{volOf(c.no)}</span>
                    <span className="h-1 w-1 rounded-full bg-background-400" />
                    <span>{c.words}</span>
                  </span>
                </span>
                <i className="ri-arrow-right-line shrink-0 text-foreground-400 transition-colors group-hover:text-primary-500" />
              </button>
            ))}
          </div>
        ) : (
          <div className="mt-8 flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 py-16">
            <i className="ri-search-eye-line text-3xl text-foreground-400" />
            <p className="text-sm text-foreground-600">没有找到匹配的章节，换个关键词试试。</p>
          </div>
        )}

        <div className="mt-8 flex justify-center">
          <button
            type="button"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-5 py-2.5 text-sm font-label text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            展开完整章节列表
            <i className="ri-arrow-down-double-line" />
          </button>
        </div>
      </div>
    </section>
  );
}